import { useParams, useNavigate, Link } from "react-router-dom"
import { useEffect, useState } from "react"
import {
  Calendar,
  Phone,
  User,
} from "lucide-react"
 
import { Button } from "../components/ui/button"
import { Card, CardContent } from "../components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { WhatsAppButton } from "../components/whatsapp-button"
 
const API_BASE = "https://pranaam.pranaamhospitals.in/wp-json/wp/v2/doctors"
 
export default function DoctorProfilePage() {
  const { slug } = useParams()
  const navigate = useNavigate()
 
  const [doctor, setDoctor] = useState(null)
  const [hospitalDoctorId, setHospitalDoctorId] = useState(null)
  const [hospitalDoctorName, setHospitalDoctorName] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
 
  useEffect(() => {
    async function fetchDoctor() {
      try {
        setLoading(true)
 
        // WP does NOT support /doctors/:slug → must query by slug
        const res = await fetch(`${API_BASE}?slug=${slug}&_embed=1`)
 
        if (!res.ok) throw new Error("Failed to fetch")
 
        const data = await res.json()
 
        if (!data || data.length === 0) {
          navigate("/404", { replace: true })
          return
        }
 
        const post = data[0]
 
        // Extract ACF & Featured Image
        const acf = post.acf || {}
 
        let featuredImage = "/placeholder.svg"
        try {
          const media = post._embedded["wp:featuredmedia"]?.[0]
          if (media) {
            featuredImage =
              media.source_url ||
              media.media_details?.sizes?.full?.source_url ||
              featuredImage
          }
        } catch (e) {}
 
        const mappedDoctor = {
          id: post.id,
          slug: post.slug,
          name: post.title.rendered,
          designation: acf.designation || "",
          specialty: acf.latest_achievement || "",
          bio: acf.overview || "",
          image: featuredImage,
 
          // Repeaters
          education: acf.education_repeater || [],
          experience: acf.experience_repeater || [],
          reviews: acf.reviews || [],
        }
 
        setDoctor(mappedDoctor)
 
           // Use doctor_id from ACF if available, otherwise lookup hospital API doctorId by name
        if (acf.doctor_id) {
          setHospitalDoctorId(acf.doctor_id)
          setHospitalDoctorName(post.title.rendered)
        } else {
          // Lookup hospital API doctorId by name (strip "Dr." prefix for API match)
          try {
            const nameForSearch = post.title.rendered
              .replace(/&nbsp;/g, ' ')
              .replace(/^Dr\.?\s*/i, '')
              .trim()
            const searchRes = await fetch(`/apis/web/doctorsbysearch?search=${encodeURIComponent(nameForSearch)}`)
            const searchData = await searchRes.json()
            const match = (searchData.result || []).find(r => 
              r.TYPE === 'DOCTOR' && 
              r.NAME.toLowerCase().includes(nameForSearch.toLowerCase())
            )
            if (match) {
              setHospitalDoctorId(match.CODE)
              setHospitalDoctorName(match.NAME)
            }
          } catch (e) {}
        }
        setLoading(false)
      } catch (err) {
        setError(err.message || "Error fetching doctor")
        setLoading(false)
      }
    }
 
    fetchDoctor()
  }, [slug])
 
  if (loading) return <div className="p-10 text-center">Loading doctor…</div>
  if (error) return <div className="p-10 text-center text-red-600">{error}</div>
  if (!doctor) return null
 
  return (
    <div className="min-h-screen bg-white">
      <main>
 
        {/* Hero */}
        <section className="py-12 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center gap-8 md:flex-row md:items-start">
             
              <div className="relative w-64 h-80 overflow-hidden rounded-lg shadow-lg shrink-0">
                <img
                  src={doctor.image}
                  alt={doctor.name}
                  className="object-cover w-full h-full"
                />
              </div>
 
              <div className="flex-1">
                <h1 className="mb-2 text-3xl font-bold">{doctor.name}</h1>
                <p className="text-primary">{doctor.designation}</p>
                <p className="mb-6 text-gray-700">{doctor.specialty}</p>
 
                <div className="flex gap-3">
                  <Link to={`/hospital-visit?doctorid=${hospitalDoctorId}`}>
                  <Button 
                    disabled={loading}
                  >
                    <Calendar className="w-5 h-5" />
                    {loading ? "Please wait..." : "Book Appointment"}
                  </Button>
                  </Link>
 
                  <Button variant="outline" asChild>
                    <a href="tel:+919848598285">
                      <Phone className="w-5 h-5" />
                      Call
                    </a>
                  </Button>
                </div>
              </div>
 
            </div>
          </div>
        </section>
 
        {/* Tabs */}
        <section className="py-12 bg-white">
          <div className="container px-4 mx-auto">
            <Tabs defaultValue="overview">
 
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                {/* <TabsTrigger value="education">Education</TabsTrigger>
                <TabsTrigger value="experience">Experience</TabsTrigger>
                <TabsTrigger value="reviews">Reviews</TabsTrigger> */}
              </TabsList>
 
              {/* Overview */}
              <TabsContent value="overview">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-4">
                      About {doctor.name}
                    </h2>
                    <div dangerouslySetInnerHTML={{ __html: doctor.bio }} />
                  </CardContent>
                </Card>
              </TabsContent>
 
              {/* Education */}
              <TabsContent value="education">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-4">Education</h2>
 
                    {doctor.education.length === 0 && (
                      <p>No education details available.</p>
                    )}
 
                    {doctor.education.map((edu, i) => (
                      <div key={i} className="mb-4">
                        <p className="font-bold">{edu.course}</p>
                        <p>{edu.institute}</p>
                        <p className="text-sm text-gray-600">{edu.year}</p>
                      </div>
                    ))}
 
                  </CardContent>
                </Card>
              </TabsContent>
 
              {/* Experience */}
              <TabsContent value="experience">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-4">Experience</h2>
 
                    {doctor.experience.length === 0 && (
                      <p>No experience details available.</p>
                    )}
 
                    {doctor.experience.map((exp, i) => (
                      <div key={i} className="mb-4">
                        <p className="font-bold">{exp.skill}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
 
              {/* Reviews */}
              <TabsContent value="reviews">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-4">Patient Reviews</h2>
 
                    {doctor.reviews.length === 0 && (
                      <p>No patient reviews available.</p>
                    )}
 
                    {doctor.reviews.map((rev, i) => (
                      <div key={i} className="mb-4">
                        <p className="italic">"{rev.patient_message}"</p>
                        <p className="font-medium">- {rev.patient_name}</p>
                      </div>
                    ))}
 
                  </CardContent>
                </Card>
              </TabsContent>
 
            </Tabs>
          </div>
        </section>
 
      </main>
 
      <WhatsAppButton />
    </div>
  )
}
 
 